import type { CSSProperties, PointerEvent, ReactNode } from 'react';
import { useRef } from 'react';

import { BlockShell } from '@/components/blocks/editor/block-shell';
import type { EditorCollectionItemDragData } from '@/components/blocks/editor/collection/collection-dnd';
import { FloatingBlockToolbarPortal } from '@/components/editor/toolbar/floating-block-toolbar-portal';
import { useEffectiveColors } from '@/hooks/use-public-design-settings';
import { useStickyHover } from '@/hooks/use-sticky-hover';
import type { CollectionItem } from '@/lib/blocks/collection/blocks';
import { getCardBgClassName } from '@/lib/blocks/collection/blocks';
import { cn } from '@/lib/utils';
import { getPlacementIndicatorClassName } from '../../dnd/drag-visuals';
import { CardActionToolbar } from '../card-action-toolbar';
import { useCollectionReorderVisual } from '../collection-reorder-visual-context';
import { useSortableCard } from './use-sortable-card';

type SortableCardDragSurfaceProps = {
  rootRef: (node: HTMLDivElement | null) => void;
  disabled?: boolean;
  isDragging: boolean;
  collectionSortableId: string;
  insertOverlay?: ReactNode;
  outerClassName?: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  listeners: { onPointerDown: (e: PointerEvent<HTMLElement>) => void };
  toolbar: ReactNode;
  blockShell: {
    isSelected: boolean;
    isHovered: boolean;
    className?: string;
  };
  content: ReactNode;
};

function SortableCardDragSurface({
  rootRef,
  disabled,
  isDragging,
  collectionSortableId,
  insertOverlay,
  outerClassName,
  onMouseEnter,
  onMouseLeave,
  listeners,
  toolbar,
  blockShell,
  content,
}: SortableCardDragSurfaceProps) {
  return (
    <div
      ref={rootRef}
      data-collection-card-editor-root
      data-collection-sortable-id={collectionSortableId}
      className={cn(
        'relative',
        !disabled && 'cursor-pointer active:cursor-grabbing',
        isDragging && 'opacity-40',
        outerClassName,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...listeners}
    >
      {insertOverlay}
      {toolbar}
      <BlockShell
        isParentLike={false}
        isSelected={blockShell.isSelected}
        isHovered={blockShell.isHovered}
        isSelectionScope={false}
        isDropHover={false}
        isDragging={false}
        className={cn('h-full', blockShell.className)}
      >
        {content}
      </BlockShell>
    </div>
  );
}

export type SortableCollectionCardRootProps = {
  item: CollectionItem;
  totalCount: number;
  sortIndex: number;
  collectionSortableGroup: string;
  collectionItemDragData: EditorCollectionItemDragData;
  disabled?: boolean;
  actionsDisabled?: boolean;
  onDelete: () => void;
  onDuplicate: () => void;
  onSelectionChange?: (selected: boolean) => void;
  /** Lifted selection: card stays primary after reorder / cancel */
  primarySelected?: boolean;
  outerClassName?: string;
  /** Override sortable registration ID (e.g. carousel loop duplicates use a unique ID). */
  sortableId?: string;
  children: ReactNode;
};

export function SortableCollectionCardRoot({
  item,
  totalCount,
  sortIndex,
  collectionSortableGroup,
  collectionItemDragData,
  disabled,
  actionsDisabled = disabled,
  onDelete,
  onDuplicate,
  onSelectionChange,
  primarySelected = false,
  outerClassName,
  sortableId,
  children,
}: SortableCollectionCardRootProps) {
  const toolbarWrapperRef = useRef<HTMLDivElement | null>(null);
  const effectiveSortableId = sortableId ?? item.id;
  const { listeners, isDragging, isSelected, mergedRef, cardRef } = useSortableCard({
    id: item.id,
    sortableId,
    index: sortIndex,
    group: collectionSortableGroup,
    dragData: collectionItemDragData,
    disabled,
    onSelectionChange,
    primarySelected,
  });

  const hoverRefsRef = useRef([cardRef, toolbarWrapperRef]);
  const { isHovered, cancelHide, scheduleHide } = useStickyHover(150, hoverRefsRef.current);

  const mergedSelected = isSelected || primarySelected;

  const { reorderDragActive, indicator } = useCollectionReorderVisual();
  const insertOverlay =
    reorderDragActive && indicator?.targetItemId === effectiveSortableId ? (
      <div className={getPlacementIndicatorClassName(indicator.edge)} aria-hidden />
    ) : null;

  // Toolbar is hover-only: local `isSelected` / lifted `primarySelected` still drive shell chrome
  // (`mergedSelected`) but must not pin the action bar open — that reads as “always on”.
  const toolbar =
    !actionsDisabled && !isDragging && isHovered ? (
      <FloatingBlockToolbarPortal anchorRef={cardRef} placement="left">
        <div ref={toolbarWrapperRef} onMouseEnter={cancelHide} onMouseLeave={scheduleHide}>
          <CardActionToolbar
            totalCount={totalCount}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            vertical
          />
        </div>
      </FloatingBlockToolbarPortal>
    ) : null;

  return (
    <SortableCardDragSurface
      rootRef={mergedRef}
      listeners={listeners}
      disabled={disabled}
      isDragging={isDragging}
      collectionSortableId={effectiveSortableId}
      insertOverlay={insertOverlay}
      outerClassName={outerClassName}
      onMouseEnter={cancelHide}
      onMouseLeave={scheduleHide}
      toolbar={toolbar}
      blockShell={{ isSelected: mergedSelected, isHovered }}
      content={children}
    />
  );
}

export function SortableCardBody({
  paddingClassName,
  cardStyle,
  children,
}: {
  paddingClassName?: string;
  cardStyle?: CSSProperties;
  children: ReactNode;
}) {
  const cardColors = useEffectiveColors();

  return (
    <div
      className={cn(
        'h-full min-w-0',
        paddingClassName,
        cardStyle?.backgroundColor && 'rounded-lg',
        getCardBgClassName(cardStyle, cardColors),
      )}
      style={cardStyle}
    >
      {children}
    </div>
  );
}
